import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import { SortDirection } from '__generated__/PackageListPageQuery.graphql';

type Sort = {
  [key: string]: SortDirection | null;
};

type SortContextType<T extends Sort> = {
  sort: T;
  setSort: Dispatch<SetStateAction<T>>;
};

type Props = {
  children?: ReactNode;
};

const SortContext = createContext<SortContextType<any>>({
  sort: {},
  setSort: () => {},
});

type UseSortableField = [
  dir: SortDirection | null,
  setSortDir: (dir: SortDirection | null) => void
];

export function useSortableField<T extends Sort>(
  name: keyof T
): UseSortableField {
  const { sort, setSort } = useContext<SortContextType<T>>(SortContext);
  const dir = sort ? sort[name] : null;

  const set = useCallback(
    (dir: SortDirection | null) => {
      setSort(value => ({
        ...value,
        [name]: dir ?? undefined,
      }));
    },
    [name, setSort]
  );

  return [dir, set];
}

export function useTableSort<T extends Sort>() {
  return useContext<SortContextType<T>>(SortContext);
}

function TableSorting<T extends Sort>({ children }: Props) {
  const [sort, setSort] = useState<T>();

  return (
    <SortContext.Provider value={{ sort, setSort }}>
      {children}
    </SortContext.Provider>
  );
}

export default TableSorting;
